import {
  fetchObservationTimelineEntryById,
  type IObservationTimelineEntity,
} from '@/api/resources/observationTimeline.api';
import { isCancelledError, type QueryClient } from '@tanstack/vue-query';
import {
  hasObservationTimelineEntity,
  updateObservationTimelineEntity,
  type TObservationTimelineCache,
} from './observationTimeline.cache';
import { observationTimelineKeys, type TObservationTimelineListKey } from './observationTimeline.keys';

export enum EObservationTimelineChange {
  Update = 'update',
  Delete = 'delete',
}

export interface IObservationTimelineChange {
  /** Update preserves date, recipients, subject, and competences; omit change for structural edits. */
  kind: EObservationTimelineChange;
  entity: IObservationTimelineEntity;
}

export const invalidateObservationTimelineQueries = async (
  queryClient: QueryClient,
  schoolClassId?: number | null,
  change?: IObservationTimelineChange,
): Promise<void> => {
  // Cancel older card reads so they cannot overwrite a newer edit or restore a deleted card.
  await queryClient.cancelQueries({
    queryKey:
      change?.kind === EObservationTimelineChange.Update && schoolClassId != null
        ? observationTimelineKeys.entity(schoolClassId, change.entity)
        : observationTimelineKeys.entries(schoolClassId),
  });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: observationTimelineKeys.stats(schoolClassId), exact: false }),
    refreshTimelineLists(queryClient, schoolClassId, change),
  ]);
};

const refreshTimelineLists = async (
  queryClient: QueryClient,
  schoolClassId?: number | null,
  change?: IObservationTimelineChange,
): Promise<void> => {
  const scope = { queryKey: observationTimelineKeys.lists(schoolClassId), exact: false };
  if (!change) return queryClient.invalidateQueries(scope);

  if (change.kind === EObservationTimelineChange.Delete) {
    await queryClient.cancelQueries(scope);
    queryClient.setQueriesData<TObservationTimelineCache>(scope, (data) =>
      updateObservationTimelineEntity(data, change.entity, null),
    );
    // Show confirmed deletions immediately; reconcile offset pagination in the background.
    void queryClient.invalidateQueries(scope);
    return;
  }

  await queryClient.invalidateQueries({ ...scope, refetchType: 'none' });
  const activeLists = queryClient.getQueryCache().findAll({ ...scope, type: 'active' });
  await Promise.all(
    activeLists.map((query) =>
      // The list prefix restricts this cache lookup to keys produced by observationTimelineKeys.list().
      refreshTimelineEntry(queryClient, query.queryKey as TObservationTimelineListKey, change.entity),
    ),
  );
};

const refreshTimelineEntry = async (
  queryClient: QueryClient,
  queryKey: TObservationTimelineListKey,
  entity: IObservationTimelineEntity,
): Promise<void> => {
  const scope = { queryKey, exact: true };
  const { schoolClassId, studentId = null, searchText, filterIncomplete, filterActiveSupportMeasures } = queryKey[3];
  const state = queryClient.getQueryState<TObservationTimelineCache>(queryKey);
  // These filters can gain or lose a card after an edit. A pending page refresh also needs to finish.
  if (
    schoolClassId == null ||
    searchText ||
    filterIncomplete ||
    filterActiveSupportMeasures ||
    state?.fetchStatus !== 'idle'
  ) {
    return queryClient.invalidateQueries(scope);
  }
  if (!hasObservationTimelineEntity(state.data, entity)) return;

  try {
    // Query keys share an in-flight read across lists, while keeping class and student projections separate.
    const entry = await queryClient.fetchQuery({
      queryKey: observationTimelineKeys.entry(schoolClassId, studentId, entity),
      queryFn: () => fetchObservationTimelineEntryById(schoolClassId, studentId, entity),
      staleTime: 0,
      gcTime: 0,
      retry: false,
    });
    queryClient.setQueryData<TObservationTimelineCache>(queryKey, (data) =>
      updateObservationTimelineEntity(data, entity, entry),
    );
    await queryClient.invalidateQueries({ ...scope, refetchType: 'none' });
  } catch (error) {
    if (!isCancelledError(error)) await queryClient.invalidateQueries(scope);
  }
};
